import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import { ProfileTemplateFieldMode } from '@up2stay/up2promo-api-client';
import { cn } from '@up2stay/up2promo-ui';
import type { ReactNode } from 'react';

import type { TemplateOverrideLabels } from '@/core/components/profile/module-entry-governance';
import { ModuleEntryPolicyControl } from '@/core/components/profile-templates/module-entry-policy-control';
import { ProfileFieldLockedHint } from '@/core/components/profile-templates/profile-field-locked-hint';
import { TemplateCustomizationHint } from '@/core/components/profile-templates/template-customization-hint';
import type { TemplateMemberValuesEditorUiProps } from '@/core/components/profile-templates/template-policy-member-controls';
import { TemplatePolicyMemberControls } from '@/core/components/profile-templates/template-policy-member-controls';
import {
  getTemplateEntryOverrideDisplay,
  isEntryPolicyLocked,
  isModuleEntryContentCustomized,
  MODULE_ENTRY_WHOLE_MODULE_KEY,
  resolveEntryPolicy,
} from '@/core/lib/profile-module-entry-policies';
import type { TemplateMemberValueColumn } from '@/core/lib/profile-template-member-values';

export type EntryGovernanceUiProps = {
  templateModule?: ProfileModuleDto;
  entryId: string;
  entryLocked?: boolean;
  entryPolicy?: ProfileTemplateFieldMode;
  showEntryPolicy?: boolean;
  onEntryPolicyChange?: (mode: ProfileTemplateFieldMode) => void;
  policyLabels?: { locked: string; free: string };
  fieldLockedHintMessage?: string;
};

export function useEntryGovernance({
  templateModule,
  entryId,
}: Pick<EntryGovernanceUiProps, 'templateModule' | 'entryId'>) {
  if (!templateModule) {
    return {
      entryLocked: false,
      entryPolicy: ProfileTemplateFieldMode.FREE,
    };
  }
  return {
    entryLocked: isEntryPolicyLocked(templateModule, entryId),
    entryPolicy: resolveEntryPolicy(templateModule, entryId),
  };
}

/** Responsive row for sortable module entries (grip, main, policy, actions). */
export function ModuleEntryListRowBody({
  isMobile = false,
  structureReadOnly = false,
  dragHandle,
  main,
  policy,
  trailing,
  className,
}: {
  isMobile?: boolean;
  structureReadOnly?: boolean;
  dragHandle?: ReactNode;
  main: ReactNode;
  policy?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('p-3', className)}>
      <div className="flex items-center gap-3">
        {!structureReadOnly && dragHandle}
        <div className="min-w-0 flex-1">{main}</div>
        {!isMobile && policy ? (
          <div className="flex shrink-0 items-center">{policy}</div>
        ) : null}
        {trailing}
      </div>
      {isMobile && policy ? (
        <div className="mt-2 flex items-center justify-end gap-2">{policy}</div>
      ) : null}
    </div>
  );
}

export function ModuleEntryPolicySlot({
  showEntryPolicy,
  entryPolicy,
  onEntryPolicyChange,
  policyLabels,
  entryId,
  entryLabel,
  memberValueModuleId,
  memberValueColumn,
  memberDialogTitle,
  memberDefaultValue,
  memberInputMaxLength,
  memberInputPlaceholder,
  workspaceId,
  templateId,
  publishStatus,
  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
  canManage = true,
}: Pick<
  EntryGovernanceUiProps,
  'showEntryPolicy' | 'entryPolicy' | 'onEntryPolicyChange' | 'policyLabels'
> &
  TemplateMemberValuesEditorUiProps & {
    entryId?: string;
    entryLabel?: string;
    memberValueModuleId?: string;
    memberValueColumn?: TemplateMemberValueColumn;
    memberDialogTitle?: string;
    memberDefaultValue?: string;
    memberInputMaxLength?: number;
    memberInputPlaceholder?: string;
    canManage?: boolean;
  }) {
  if (!showEntryPolicy || !policyLabels || !onEntryPolicyChange) return null;

  const resolvedMemberColumn =
    memberValueColumn ??
    (entryId &&
    workspaceId != null &&
    templateId != null &&
    publishStatus != null &&
    memberValuesDict != null
      ? ({
          kind: 'moduleEntry',
          entryId,
          moduleId: memberValueModuleId,
          label: entryLabel ?? entryId,
        } satisfies TemplateMemberValueColumn)
      : undefined);

  if (resolvedMemberColumn) {
    return (
      <TemplatePolicyMemberControls
        value={entryPolicy}
        onChange={onEntryPolicyChange}
        labels={policyLabels}
        memberValueColumn={resolvedMemberColumn}
        memberDialogTitle={memberDialogTitle ?? resolvedMemberColumn.label}
        memberDefaultValue={memberDefaultValue}
        workspaceId={workspaceId}
        templateId={templateId}
        publishStatus={publishStatus}
        memberRoster={memberRoster}
        memberRosterLoading={memberRosterLoading}
        hasAssignedTeams={hasAssignedTeams}
        memberValuesDict={memberValuesDict}
        canManage={canManage}
        memberInputMaxLength={memberInputMaxLength}
        memberInputPlaceholder={memberInputPlaceholder}
      />
    );
  }

  return (
    <ModuleEntryPolicyControl
      value={entryPolicy}
      onChange={onEntryPolicyChange}
      labels={policyLabels}
    />
  );
}

export function ModuleEntryLockedHint({
  entryLocked,
  fieldLockedHintMessage,
  className,
}: Pick<EntryGovernanceUiProps, 'entryLocked' | 'fieldLockedHintMessage'> & {
  className?: string;
}) {
  if (!entryLocked || !fieldLockedHintMessage) return null;
  return (
    <ProfileFieldLockedHint
      locked
      message={fieldLockedHintMessage}
      className={className}
    />
  );
}

export function ModuleEntryOverrideHint({
  currentModule,
  templateModule,
  entryId,
  showEntryOverrideHints = false,
  overrideHintMessage,
  templateOverrideLabels,
  onReset,
  className,
}: {
  currentModule: ProfileModuleDto;
  templateModule?: ProfileModuleDto;
  entryId: string;
  showEntryOverrideHints?: boolean;
  overrideHintMessage?: string;
  templateOverrideLabels?: TemplateOverrideLabels;
  onReset: () => void;
  className?: string;
}) {
  if (
    !showEntryOverrideHints ||
    !templateModule ||
    !templateOverrideLabels ||
    !overrideHintMessage
  ) {
    return null;
  }

  const customized = isModuleEntryContentCustomized(
    currentModule,
    templateModule,
    entryId,
  );
  const display =
    customized && templateOverrideLabels.entryFields
      ? getTemplateEntryOverrideDisplay(
          currentModule,
          templateModule,
          entryId,
          templateOverrideLabels.entryFields,
        )
      : null;

  return (
    <TemplateCustomizationHint
      customized={customized && Boolean(display)}
      message={overrideHintMessage}
      resetLabel={templateOverrideLabels.reset}
      templateValueLabel={templateOverrideLabels.templateValue}
      templateValue={display?.single}
      templateDetails={display?.details}
      onReset={onReset}
      className={className}
    />
  );
}

export function WholeModuleGovernanceHeader({
  templateModule,
  showEntryPolicy = false,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  entryLabel,
  enableMemberValues = true,
  memberValueColumn,
  memberDialogTitle,
  memberDefaultValue,
  memberInputMaxLength,
  memberInputPlaceholder,
  workspaceId,
  templateId,
  publishStatus,
  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
  canManage = true,
}: Omit<EntryGovernanceUiProps, 'entryId' | 'onEntryPolicyChange'> &
  TemplateMemberValuesEditorUiProps & {
    onEntryPolicyChange?: (
      entryId: string,
      mode: ProfileTemplateFieldMode,
    ) => void;
    entryLabel?: string;
    /** When false, only policy select is shown (e.g. images/files modules). */
    enableMemberValues?: boolean;
    memberValueColumn?: TemplateMemberValueColumn;
    memberDialogTitle?: string;
    memberDefaultValue?: string;
    memberInputMaxLength?: number;
    memberInputPlaceholder?: string;
    canManage?: boolean;
  }) {
  const { entryLocked, entryPolicy } = useEntryGovernance({
    templateModule,
    entryId: MODULE_ENTRY_WHOLE_MODULE_KEY,
  });

  const handlePolicyChange = onEntryPolicyChange
    ? (mode: ProfileTemplateFieldMode) =>
        onEntryPolicyChange(MODULE_ENTRY_WHOLE_MODULE_KEY, mode)
    : undefined;

  if (!showEntryPolicy && !entryLocked) return null;

  const memberEditorProps = enableMemberValues
    ? {
        entryId: MODULE_ENTRY_WHOLE_MODULE_KEY,
        entryLabel,
        memberValueModuleId: templateModule?.id,
        memberValueColumn,
        memberDialogTitle,
        memberDefaultValue,
        memberInputMaxLength,
        memberInputPlaceholder,
        workspaceId,
        templateId,
        publishStatus,
        memberRoster,
        memberRosterLoading,
        hasAssignedTeams,
        memberValuesDict,
        canManage,
      }
    : {};

  return (
    <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
      <ModuleEntryLockedHint
        entryLocked={entryLocked}
        fieldLockedHintMessage={fieldLockedHintMessage}
      />
      <ModuleEntryPolicySlot
        showEntryPolicy={showEntryPolicy}
        entryPolicy={entryPolicy}
        onEntryPolicyChange={handlePolicyChange}
        policyLabels={policyLabels}
        {...memberEditorProps}
      />
    </div>
  );
}
